<template>
  <v-container>
    <v-dialog v-model="dialog" width="950px" persistent :retain-focus="false">
      <v-card class="custom-card survey-dialog-card" rounded="0">

<v-toolbar color="primary" class="white--text survey-modal-toolbar">
  <v-toolbar-title class="font-weight-bold survey-modal-title">
    {{ updateData && updateData._id
      ? $t("esgPlatform.editSurvey")
      : $t("esgPlatform.addNewSurvey")
    }}
  </v-toolbar-title>

  <v-spacer />

  <v-btn
    icon
    class="white--text survey-close-btn"
    @click="closeModal"
  >
    <v-icon>mdi-close</v-icon>
  </v-btn>
</v-toolbar>

        <v-form ref="addNewSurvey" lazy-validation autocomplete="off">
          <v-card-text class="black--text survey-modal-body">
            <section class="survey-section">
              <div
                class="section-title"
              >
                <span>{{ $t('esgPlatform.basicSurveySettings') }}</span>
                <button
                  type="button"
                  class="section-title__toggle"
                  :aria-expanded="!collapsed.basic"
                  aria-label="Toggle Basic Survey Settings"
                  @click="toggleSection('basic')"
                >
                  <v-icon class="section-title__icon" :class="{ 'section-title__icon--collapsed': collapsed.basic }">mdi-menu-up</v-icon>
                </button>
              </div>

              <div v-show="!collapsed.basic">
              <v-row class="survey-grid">
                <v-col cols="12" md="6" class="survey-grid__col">
                  <field-label :label="$t('esgPlatform.surveyTitlePlaceholder')" required :tooltip="$t('esgPlatform.surveyTitleFieldTT')" />
                  <v-text-field
                    outlined
                    v-model="survey.title"
                    :rules="rules.title"
                    placeholder="Enter here"
                    hide-details="auto"
                    class="survey-field"
                  />

                  <field-label :label="$t('esgPlatform.surveyDescription')" :tooltip="$t('esgPlatform.surveyDescriptionTT')" />
                  <v-card outlined class="survey-editor-card">
                    <quill-editor
                      v-model="survey.description"
                      ref="descriptionEditor"
                      :options="quillOptions($t('esgPlatform.describeHere'))"
                    />
                  </v-card>

                </v-col>

                <v-col cols="12" md="6" class="survey-grid__col">
                  <field-label :label="$t('esgPlatform.typeOfSurvey')" required :tooltip="$t('esgPlatform.typeOfTheSurvey')" />
                  <v-select
                    :items="surveyTypes"
                    item-text="label"
                    item-value="value"
                    outlined
                    v-model="survey.type"
                    :rules="rules.required"
                    :readonly="isEsgContext"
                    placeholder="Select"
                    hide-details="auto"
                    class="survey-field"
                  />

                  <field-label :label="$t('esgPlatform.surveyInstructions')" :tooltip="$t('esgPlatform.surveyInstructionsTT')" />
                  <v-card outlined class="survey-editor-card">
                    <quill-editor
                      v-model="survey.instructions"
                      ref="instructionsEditor"
                      :options="quillOptions($t('esgPlatform.describeHere'))"
                    />
                  </v-card>
                </v-col>
              </v-row>
              </div>
            </section>

            <section class="survey-section">
              <div
                class="section-title"
              >
                <span>{{ $t('esgPlatform.advancedSurveySettings') }}</span>
                <button
                  type="button"
                  class="section-title__toggle"
                  :aria-expanded="!collapsed.advanced"
                  aria-label="Toggle Advanced Survey Settings"
                  @click="toggleSection('advanced')"
                >
                  <v-icon class="section-title__icon" :class="{ 'section-title__icon--collapsed': collapsed.advanced }">mdi-menu-up</v-icon>
                </button>
              </div>

              <div v-show="!collapsed.advanced">
              <v-row class="survey-grid">
                <v-col cols="12" md="6" class="survey-grid__col">
                  <div class="setting-block">
                    <field-label :label="$t('esgPlatform.surveyStatus')" :tooltip="$t('esgPlatform.activationStatusOfSurvey')" />
                    <div class="setting-toggle">
                      <span class="setting-toggle__label">{{ survey.isDisabled ? $t('esgPlatform.inactive') : $t('esgPlatform.active') }}</span>
                      <v-switch inset v-model="enabled" @change="flipDisabled" hide-details class="score-switch" />
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="survey-grid__col">
                  <div class="setting-block">
                    <field-label :label="$t('esgPlatform.surveyScore')" :tooltip="$t('esgPlatform.surveyScoreTT')" />
                    <div class="setting-toggle">
                      <span class="setting-toggle__label">{{ score ? $t('esgPlatform.enabled') : $t('esgPlatform.disabled') }}</span>
                      <v-switch inset v-model="score" hide-details class="score-switch" />
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="survey-inner-divider" />

              <div class="schedule-control">
                <field-label :label="$t('esgPlatform.scheduleEnabledLabel')" :tooltip="$t('esgPlatform.selectStartEndDates')" />
                <v-switch inset v-model="survey.scheduleEnabled" hide-details class="score-switch" />
              </div>
              <div v-if="survey.scheduleEnabled" class="schedule-date-list">
                <div>
                  <button
                    type="button"
                    class="schedule-date-link"
                    :class="{ 'schedule-date-link--error': scheduleStartDateError }"
                    @click="openScheduleDatePicker('start')"
                  >
                    <v-icon class="schedule-date-link__icon">mdi-calendar-month-outline</v-icon>
                    <span>
                      {{ $t('esgPlatform.startDateLabel') }}
                      <span v-if="survey.startDate" class="schedule-date-link__value">
                        {{ formatScheduleDate(survey.startDate) }}
                      </span>
                    </span>
                  </button>
                  <div v-if="scheduleStartDateError" class="schedule-date-error">
                    {{ scheduleStartDateError }}
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    class="schedule-date-link"
                    :class="{ 'schedule-date-link--error': scheduleEndDateError }"
                    @click="openScheduleDatePicker('end')"
                  >
                    <v-icon class="schedule-date-link__icon">mdi-calendar-month-outline</v-icon>
                    <span>
                      {{ $t('esgPlatform.endDate') }}
                      <span v-if="survey.endDate" class="schedule-date-link__value">
                        {{ formatScheduleDate(survey.endDate) }}
                      </span>
                    </span>
                  </button>
                  <div v-if="scheduleEndDateError" class="schedule-date-error">
                    {{ scheduleEndDateError }}
                  </div>
                </div>
              </div>
              </div>
            </section>

            <section class="survey-section">
              <div
                class="section-title"
              >
                <span>{{ $t('esgPlatform.moduleIntegration') }}</span>
                <button
                  type="button"
                  class="section-title__toggle"
                  :aria-expanded="!collapsed.module"
                  aria-label="Toggle Module Integration"
                  @click="toggleSection('module')"
                >
                  <v-icon class="section-title__icon" :class="{ 'section-title__icon--collapsed': collapsed.module }">mdi-menu-up</v-icon>
                </button>
              </div>

              <div v-show="!collapsed.module">
              <v-row class="survey-grid">
                <v-col v-if="showAttachedModuleField" cols="12" md="6" class="survey-grid__col">
                  <field-label
                    :label="$t('esgPlatform.attachSurveyToModule')"
                    :required="isAttachedModuleRequired"
                    :tooltip="$t('esgPlatform.selectDimitraModuleTT')"
                  />
                  <v-select
                    :items="moduleOptions"
                    item-text="label"
                    item-value="value"
                    outlined
                    v-model="survey.attachedModule"
                    :rules="attachedModuleRules"
                    :no-data-text="attachedModuleNoDataText"
                    placeholder="Select"
                    readonly
                    hide-details="auto"
                  />
                </v-col>
                <!--
                <v-col cols="12" md="6" class="survey-grid__col">
                  <field-label label="Select ESG Program" tooltip="Existing ESG Program selection" />
                  <v-select
                    :items="['CSRD']"
                    :value="'CSRD'"
                    outlined
                    readonly
                    class="esg-program-display"
                    hide-details
                  />
                </v-col>
                -->
                <v-col v-if="showRespondentTypeField" cols="12" md="6" class="survey-grid__col">
                <field-label :label="$t('esgPlatform.whoWillAnswerThisSurvey')" required :tooltip="$t('esgPlatform.whoWillAnswerThisSurveyTT')" />
                  <v-select
                    :items="respondentTypes"
                    item-text="label"
                    item-value="value"
                    outlined
                    v-model="survey.respondentType"
                    :rules="respondentTypeRules"
                    placeholder="Select"
                    hide-details="auto"
                  />
                </v-col>

                  <v-radio-group v-if="showRespondentSelectionOptions" v-model="survey.respondentSelectionType" class="survey-radio-group" hide-details>
                    <v-radio
                      v-for="item in respondentSelectionOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    />
                  </v-radio-group>

                  <div class="d-flex align-center">
                    <v-btn
                      v-if="showSelectionButton"
                      color="primary"
                      class="mt-10 ml-5 select-users-btn"
                      height="38"
                      depressed
                      @click="openSelectionDialog"
                    >
                      {{ selectionButtonLabel }}
                    </v-btn>
                    <span v-if="selectionCountText" class="text-caption grey--text text--darken-1">
                      {{ selectionCountText }}
                    </span>
                  </div>
              </v-row>

              </div>
            </section>

            <section class="survey-section">
              <div
                class="section-title"
              >
                <span>{{ $t('esgPlatform.questionFeatures') }}</span>
                <button
                  type="button"
                  class="section-title__toggle"
                  :aria-expanded="!collapsed.questions"
                  aria-label="Toggle Question Features"
                  @click="toggleSection('questions')"
                >
                  <v-icon class="section-title__icon" :class="{ 'section-title__icon--collapsed': collapsed.questions }">mdi-menu-up</v-icon>
                </button>
              </div>

              <div v-show="!collapsed.questions">
              <v-row class="survey-grid">
                <v-col cols="12" md="6" class="survey-grid__col">
                  <field-label :label="$t('esgPlatform.selectPostAnswerActions')" :tooltip="$t('esgPlatform.actionsAfterAnswerTT')" />
                  <v-select
                    :items="postAnswerActionOptions"
                    item-text="label"
                    item-value="value"
                    outlined
                    multiple
                    v-model="survey.postAnswerActions"
                    placeholder="Select"
                    hide-details="auto"
                  />

                  <div class="setting-block setting-block--below-select">
                    <field-label :label="$t('esgPlatform.repeatQuestionFunctionality')" :tooltip="$t('esgPlatform.enableRepeatedQuestionsTT')" />
                    <div class="setting-toggle">
                      <span class="setting-toggle__label">{{ survey.isRepeatQuestionEnabled ? $t('esgPlatform.enabled') : $t('esgPlatform.disabled') }}</span>
                      <v-switch inset v-model="survey.isRepeatQuestionEnabled" hide-details class="score-switch" />
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="6" class="survey-grid__col">
                  <field-label :label="$t('esgPlatform.selectQuestionTypesLabel')" :tooltip="$t('esgPlatform.questionTypesAvailableTT')" />
                  <v-select
                    :items="questionTypeOptions"
                    item-text="label"
                    item-value="value"
                    outlined
                    multiple
                    v-model="survey.allowedQuestionTypes"
                    placeholder="Select"
                    @change="normalizeQuestionTypes"
                    hide-details="auto"
                  />

                </v-col>
              </v-row>
              </div>
            </section>

            <v-card-actions class="survey-actions">
              <v-spacer></v-spacer>
              <v-btn outlined class="survey-btn survey-btn--secondary" depressed color="primary" @click="closeModal">
                {{ $t("cancel") }}
              </v-btn>
              <v-btn class="survey-btn survey-btn--primary" depressed color="primary" @click="saveNewSurvey">
                {{ updateData && updateData._id ? $t("save") : $t("surveyBuilder.next") }}
              </v-btn>
            </v-card-actions>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="scheduleDateDialog"
      width="610"
      max-width="calc(100vw - 12px)"
      :retain-focus="false"
      @click:outside="closeScheduleDatePicker"
    >
      <v-card class="schedule-date-dialog-card" rounded="0">
        <v-toolbar color="primary" class="white--text schedule-date-dialog-toolbar" flat>
          <v-toolbar-title class="font-weight-bold schedule-date-dialog-title">
            {{ $t('esgPlatform.chooseDate') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            class="white--text schedule-date-dialog-close"
            @click="closeScheduleDatePicker"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="schedule-date-dialog-body">
          <v-date-picker
            v-model="scheduleDraftDate"
            class="schedule-calendar"
            color="primary"
            no-title
            show-adjacent-months
            full-width
            :min="schedulePickerMin"
          />

          <div class="schedule-selected-date">
            <label class="schedule-selected-date__label">{{ $t('esgPlatform.selectedDate') }}</label>
            <div class="schedule-selected-date__value">
              <button
                v-if="scheduleDraftDate"
                type="button"
                class="schedule-selected-date__clear"
                aria-label="Clear selected date"
                @click="clearScheduleDraftDate"
              >
                <v-icon>mdi-close-circle</v-icon>
              </button>
              <span v-if="scheduleDraftDate">{{ scheduleDraftDateLabel }}</span>
            </div>
          </div>

          <v-btn
            class="schedule-date-dialog-select"
            depressed
            color="primary"
            :disabled="!scheduleDraftDate"
            @click="confirmScheduleDate"
          >
            {{ $t('esgPlatform.selectBtn') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <SelectCooperatives
      v-if="selectSubOrganizationsModal"
      :dialogI="selectSubOrganizationsModal"
      :selectedIds="survey.selectedSubOrganizationIds"
      :selectAll="survey.selectAllSubOrganizations"
      @closeDialog="selectSubOrganizationsModal = false"
      @cooperativesSelected="setSelectedSubOrganizations"
    />

    <SelectSurveyUsers
      v-if="selectUsersModal"
      :dialogI="selectUsersModal"
      :selectedIds="survey.selectedSurveyUserIds"
      :selectAll="survey.selectAllSurveyUsers"
      :initialFilter="survey.surveyUserFilter"
      :autoCondition="survey.surveyUserAutoCondition"
      @closeDialog="selectUsersModal = false"
      @usersSelected="setSelectedSurveyUsers"
    />
  </v-container>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import EsgService from "@/_services/EsgService";
import { quillEditor } from "vue-quill-editor";
import { VIcon, VTooltip } from "vuetify/lib";
import SelectCooperatives from "./SelectCooperatives.vue";
import SelectSurveyUsers from "./SelectSurveyUsers.vue";
import {
  SURVEY_MODULES,
  cloneSurveyBuilderConfig,
  getDefaultRespondentTypeForModule,
  getAudienceTypeConfig,
  getDefaultRespondentSelectionType,
  getPostAnswerActionOptionsForAudience,
  getQuestionTypeOptionsForAudience,
  getRespondentTypeOptionsForModule,
  getRespondentSelectionOption,
  getRespondentSelectionOptions,
  isAttachedModuleAllowedForAudience,
  isRespondentTypeAllowedForModule,
  normalizePostAnswerActionsForAudience,
  normalizeQuestionTypesForAudience,
  normalizeSurveyBuilderConfig,
  shouldShowRespondentSelectionForModule,
  shouldShowRespondentTypeForModule,
  validateAttachedModuleValue,
} from "../constants/surveyBuilderConfig";

const surveyTypeMap = {
  STANDARD: "Standard Survey",
  RISK_ASSESSMENT: "Risk Assessment Survey",
  ESG_GOAL: "ESG Goal Survey",
  RWA: "RWA Survey",
};

const respondentTypeValueMap = {
  "Connected Farmer": "Connected_Farmer",
  "Connected Cacao": "Connected_Cacao",
};

const normalizeRespondentTypeValue = (value) => respondentTypeValueMap[value] || value;

const toDateInputValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const FieldLabel = {
  name: "FieldLabel",
  props: {
    label: { type: String, required: true },
    tooltip: { type: String, default: "" },
    required: { type: Boolean, default: false },
  },
  render(h) {
    const children = [
      h("label", { class: "field-label__text" }, [
        this.label,
        this.required ? h("span", { class: "red--text field-label__required" }, "*") : null,
      ]),
    ];

    if (this.tooltip) {
      children.push(h(VTooltip, {
        props: {
          top: true,
          color: "#787878",
          maxWidth: "350",
        },
        scopedSlots: {
          activator: ({ on, attrs }) => h(VIcon, {
            class: "icon-with-background field-label__tooltip-icon",
            style: { color: "#0EBF67" },
            attrs: {
              ...attrs,
              tabindex: "0",
              role: "button",
              "aria-label": this.tooltip,
            },
            on,
          }, "mdi-alert-circle"),
        },
      }, [h("span", this.tooltip)]));
    }

    return h("div", { class: "field-label" }, children);
  },
};

export default {
  components: {
    "quill-editor": quillEditor,
    FieldLabel,
    SelectCooperatives,
    SelectSurveyUsers,
  },
  mixins: [loadingMixin],
  props: {
    dialogI: {
      type: Boolean,
      required: true,
    },
    updateData: {
      required: false,
      type: Object,
    },
    hasQuestions: {
      required: false,
      type: Boolean,
    },
    fromAddGoals: {
      required: false,
      type: Boolean,
    },
    // "esg"  -> ESG Goal Survey builder (type + module locked to ESG)
    // "standard" -> general survey builder (type editable among the 3 non-ESG
    //               types, module locked to "Survey")
    surveyContext: {
      required: false,
      type: String,
      default: "standard",
    },
  },
  data() {
    return {
      orgId: null,
      loggedInUser: null,
      enabled: false,
      score: false,
      isHydratingSurvey: false,
      selectUsersModal: false,
      selectSubOrganizationsModal: false,
      scheduleDateDialog: false,
      scheduleDateMode: "start",
      scheduleDraftDate: null,
      scheduleValidationTouched: false,
      collapsed: {
        basic: false,
        advanced: false,
        module: false,
        questions: false,
      },
      allSurveyTypes: [
        { label: this.$t("esgPlatform.standardLabel"), value: "Standard Survey" },
        { label: this.$t("esgPlatform.riskAssessmentSurvey"), value: "Risk Assessment Survey" },
        { label: this.$t("esgPlatform.esgGoalSurvey"), value: "ESG Goal Survey" },
        { label: this.$t("esgPlatform.rwaSurvey"), value: "RWA Survey" },
      ],
      surveyBuilderConfig: cloneSurveyBuilderConfig(),
      survey: this.defaultSurvey(),
      rules: {
        title: [
          (v) => !!v || this.$t("errors.required"),
          (v) => (v && v.trim().length > 0) || this.$t("errors.required"),
          (v) => (v && v.length < 100) || this.$t("surveyBuilder.surveyTitleLength"),
        ],
        required: [
          (v) => !!v || this.$t("errors.required"),
        ],
      },
    };
  },
  computed: {
    dialog() {
      return this.dialogI;
    },
    // In edit mode the context follows the survey being edited; otherwise it
    // follows the caller (ESG goals flow / ESG list -> "esg").
    resolvedContext() {
      if (this.fromAddGoals) return "esg";
      if (this.updateData && this.updateData._id) {
        const type = surveyTypeMap[this.updateData.type] || this.updateData.type;
        return type === "ESG Goal Survey" ? "esg" : "standard";
      }
      return this.surveyContext === "esg" ? "esg" : "standard";
    },
    isEsgContext() {
      return this.resolvedContext === "esg";
    },
    // ESG context -> only "ESG Goal Survey"; standard context -> the three
    // non-ESG types.
    surveyTypes() {
      if (this.isEsgContext) {
        return this.allSurveyTypes.filter((t) => t.value === "ESG Goal Survey");
      }
      return this.allSurveyTypes.filter((t) => t.value !== "ESG Goal Survey");
    },
    // The module is locked to the context default ("ESG" / "Survey"). Keep the
    // survey's current module in the list too, so edit mode never blanks out a
    // legacy value.
    lockedModuleValue() {
      return this.isEsgContext ? SURVEY_MODULES.ESG_PLATFORM.value : SURVEY_MODULES.SURVEY.value;
    },
    today() {
      return new Date().toISOString().split("T")[0];
    },
    schedulePickerMin() {
      return this.scheduleDateMode === "end"
        ? this.survey.startDate || this.today
        : this.today;
    },
    scheduleDraftDateLabel() {
      return this.formatScheduleDate(this.scheduleDraftDate);
    },
    respondentTypes() {
      return getRespondentTypeOptionsForModule(this.survey.attachedModule, this.surveyBuilderConfig);
    },
    currentAudienceConfig() {
      return getAudienceTypeConfig(this.survey.respondentType, this.surveyBuilderConfig);
    },
    respondentSelectionOptions() {
      return getRespondentSelectionOptions(this.survey.respondentType, this.surveyBuilderConfig);
    },
    currentRespondentSelectionOption() {
      return getRespondentSelectionOption(
        this.survey.respondentType,
        this.survey.respondentSelectionType,
        this.surveyBuilderConfig
      );
    },
    moduleOptions() {
      const options = this.surveyBuilderConfig.moduleOptions || [];
      const scoped = options.filter((option) => option.value === this.lockedModuleValue);
      // Preserve a legacy/other module value if the survey already has one that
      // is not part of the scoped list, so the (locked) select still displays it.
      if (
        this.survey.attachedModule
        && !scoped.some((option) => option.value === this.survey.attachedModule)
      ) {
        const current = options.find((option) => option.value === this.survey.attachedModule);
        if (current) {
          return [current, ...scoped];
        }
      }
      return scoped;
    },
    postAnswerActionOptions() {
      return getPostAnswerActionOptionsForAudience(this.survey.respondentType, this.surveyBuilderConfig);
    },
    questionTypeOptions() {
      return getQuestionTypeOptionsForAudience(this.survey.respondentType, this.surveyBuilderConfig);
    },
    showAttachedModuleField() {
      return true;
    },
    showRespondentTypeField() {
      return Boolean(this.survey.attachedModule)
        && shouldShowRespondentTypeForModule(this.survey.attachedModule, this.surveyBuilderConfig);
    },
    isEsgPlatformSelected() {
      return this.survey.attachedModule === SURVEY_MODULES.ESG_PLATFORM.value;
    },
    showRespondentSelectionOptions() {
      return Boolean(this.survey.attachedModule)
        && !this.isEsgPlatformSelected
        && shouldShowRespondentSelectionForModule(this.survey.attachedModule, this.surveyBuilderConfig);
    },
    isAttachedModuleRequired() {
      return true;
    },
    attachedModuleNoDataText() {
      return this.currentAudienceConfig.messages?.noModuleOptions || this.$t("esgPlatform.noModuleOptionsAvailable");
    },
    attachedModuleRules() {
      return [
        (value) => validateAttachedModuleValue(
          this.survey.respondentType,
          value,
          this.surveyBuilderConfig
        ),
      ];
    },
    respondentTypeRules() {
      return [
        (value) => !!value || this.$t("errors.required"),
        (value) => isRespondentTypeAllowedForModule(
          this.survey.attachedModule,
          value,
          this.surveyBuilderConfig
        ) || this.$t("esgPlatform.selectedAnswererNotAvailable"),
      ];
    },
    showSelectionButton() {
      return this.showRespondentSelectionOptions
        && Boolean(this.currentRespondentSelectionOption?.requiresSelection);
    },
    selectionButtonLabel() {
      return this.currentRespondentSelectionOption?.buttonLabel || "Select";
    },
    selectionCountText() {
      if (!this.showRespondentSelectionOptions) return "";

      const selectionOption = this.currentRespondentSelectionOption;
      if (!selectionOption?.requiresSelection || !selectionOption.selectionKey) return "";

      const selectedItems = this.survey[selectionOption.selectionKey] || [];
      return selectedItems.length
        ? `${selectedItems.length} ${selectionOption.selectedLabel} selected`
        : "";
    },
    scheduleStartDateError() {
      if (!this.scheduleValidationTouched || !this.survey.scheduleEnabled || this.survey.startDate) {
        return "";
      }
      return this.$t("esgPlatform.startDateRequired");
    },
    scheduleEndDateError() {
      if (!this.scheduleValidationTouched || !this.survey.scheduleEnabled) {
        return "";
      }
      if (!this.survey.endDate) {
        return this.$t("esgPlatform.endDateRequired");
      }
      if (
        this.survey.startDate &&
        new Date(this.survey.endDate) < new Date(this.survey.startDate)
      ) {
        return this.$t("esgPlatform.endDateBeforeStartDate");
      }
      return "";
    },
  },
  watch: {
    "survey.attachedModule"(value, previousValue) {
      if (this.isHydratingSurvey) return;
      if (value !== previousValue) {
        this.handleAttachedModuleChange(value, true);
      }
    },
    "survey.respondentType"(value, previousValue) {
      if (this.isHydratingSurvey) return;
      if (value !== previousValue) {
        this.handleRespondentTypeChange(value);
      }
    },
    "survey.scheduleEnabled"(enabled) {
      if (!enabled) {
        this.survey.startDate = null;
        this.survey.endDate = null;
        this.scheduleValidationTouched = false;
        this.scheduleDateDialog = false;
        this.scheduleDraftDate = null;
      }
    },
    "survey.startDate"(startDate) {
      if (
        startDate &&
        this.survey.endDate &&
        new Date(this.survey.endDate) < new Date(startDate)
      ) {
        this.survey.endDate = null;
      }
    },
  },
  mounted() {
    this.isHydratingSurvey = true;
    const userInfo = JSON.parse(localStorage.getItem("user"));
    this.loggedInUser = userInfo.id;
    this.orgId = userInfo.user_organization.id;

    if (this.updateData && Object.keys(this.updateData).length && this.updateData.title !== "Loading...") {
      this.survey = this.normalizeSurvey(this.updateData);
    } else {
      this.survey = this.defaultSurvey();
      if (this.isEsgContext) {
        this.survey.type = "ESG Goal Survey";
        this.survey.attachedModule = SURVEY_MODULES.ESG_PLATFORM.value;
      } else {
        this.survey.type = "Standard Survey";
        this.survey.attachedModule = SURVEY_MODULES.SURVEY.value;
      }
    }

    this.enabled = !this.survey.isDisabled;
    this.score = this.survey.isScoreEnabled;
    this.handleAttachedModuleChange(this.survey.attachedModule, false);
    this.handleRespondentTypeChange(this.survey.respondentType, false);
    this.loadSurveyBuilderConfig();
  },
  methods: {
    toggleSection(key) {
      this.collapsed[key] = !this.collapsed[key];
    },
    async loadSurveyBuilderConfig() {
      try {
        const response = await EsgService.getAssessmentSurveyBuilderConfig();
        const config = response?.data;

        if (config?.audienceTypeConfig) {
          this.surveyBuilderConfig = normalizeSurveyBuilderConfig(config);
          this.handleAttachedModuleChange(this.survey.attachedModule, false);
          this.handleRespondentTypeChange(this.survey.respondentType, false);
        }
      } catch (err) {
        console.warn("Using local survey builder configuration fallback", err);
      } finally {
        this.$nextTick(() => {
          this.isHydratingSurvey = false;
        });
      }
    },
    defaultSurvey() {
      return {
        orgId: null,
        createdBy: null,
        title: null,
        description: null,
        instructions: null,
        type: "Standard Survey",
        respondentType: "Farmer (Single response for all farms)",
        respondentSelectionType: "All Users",
        selectedSubOrganizationIds: [],
        selectedSurveyUserIds: [],
        selectAllSubOrganizations: false,
        selectAllSurveyUsers: false,
        isDisabled: true,
        isScoreEnabled: false,
        scheduleEnabled: false,
        startDate: null,
        endDate: null,
        attachedModule: null,
        postAnswerActions: ["Recommendations", "Get Data"],
        allowedQuestionTypes: ["all"],
        isRepeatQuestionEnabled: false,
        surveyUserFilter: {},
        surveyUserAutoCondition: {},
        subOrganizationFilter: {},
        multiStepType: null,
        multiStepQuestionCount: null,
      };
    },
    normalizeSurvey(data) {
      const scheduleEnabled = hasOwn(data, "scheduleEnabled") ? data.scheduleEnabled : data.isScheduleEnabled;
      const startDate = hasOwn(data, "startDate") ? data.startDate : data.scheduleStartDate;
      const endDate = hasOwn(data, "endDate") ? data.endDate : data.scheduleEndDate;
      const normalizedScheduleEnabled = Boolean(scheduleEnabled);
      const respondentType = normalizeRespondentTypeValue(data.respondentType);

      return {
        ...this.defaultSurvey(),
        ...data,
        type: surveyTypeMap[data.type] || data.type || "Standard Survey",
        respondentType,
        respondentSelectionType: data.respondentSelectionType || (respondentType === "Sub-Organization" ? "All Sub-Organizations" : "All Users"),
        selectedSubOrganizationIds: Array.isArray(data.selectedSubOrganizationIds) ? data.selectedSubOrganizationIds : [],
        selectedSurveyUserIds: Array.isArray(data.selectedSurveyUserIds) ? data.selectedSurveyUserIds : [],
        postAnswerActions: Array.isArray(data.postAnswerActions) ? data.postAnswerActions : ["Recommendations", "Get Data"],
        allowedQuestionTypes: Array.isArray(data.allowedQuestionTypes) && data.allowedQuestionTypes.length ? data.allowedQuestionTypes : ["all"],
        scheduleEnabled: normalizedScheduleEnabled,
        startDate: normalizedScheduleEnabled ? toDateInputValue(startDate) : null,
        endDate: normalizedScheduleEnabled ? toDateInputValue(endDate) : null,
        surveyUserFilter: data.surveyUserFilter || {},
        surveyUserAutoCondition: data.surveyUserAutoCondition || {},
        subOrganizationFilter: data.subOrganizationFilter || {},
      };
    },
    quillOptions(placeholder) {
      return {
        theme: "snow",
        placeholder,
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ header: [1, 2, 3, false] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ color: [] }, { background: [] }],
            ["clean"],
          ],
        },
      };
    },
    flipDisabled() {
      this.survey.isDisabled = !this.enabled;
    },
    handleAttachedModuleChange(value, reset = true) {
      const defaultRespondentType = getDefaultRespondentTypeForModule(value, this.surveyBuilderConfig);

      if (
        defaultRespondentType
        && (
          !this.showRespondentTypeField
          || !this.survey.respondentType
          || !isRespondentTypeAllowedForModule(value, this.survey.respondentType, this.surveyBuilderConfig)
        )
      ) {
        this.survey.respondentType = defaultRespondentType;
      }

      if (!this.showRespondentTypeField && !this.showRespondentSelectionOptions) {
        this.survey.respondentSelectionType = getDefaultRespondentSelectionType(
          this.survey.respondentType,
          this.surveyBuilderConfig
        );
      }

      if (reset) {
        this.clearAudienceSelectionFields();
      }

      this.handleRespondentTypeChange(this.survey.respondentType, reset);
    },
    handleRespondentTypeChange(value, reset = true) {
      const audienceConfig = getAudienceTypeConfig(value, this.surveyBuilderConfig);
      const respondentSelection = audienceConfig.respondentSelection || {};
      const allowedSelectionTypes = respondentSelection.allowedValues || [];

      if (!this.showRespondentSelectionOptions) {
        if (reset) this.clearAudienceSelectionFields();
      } else if (reset || !allowedSelectionTypes.includes(this.survey.respondentSelectionType)) {
        this.survey.respondentSelectionType = getDefaultRespondentSelectionType(value, this.surveyBuilderConfig);
      }

      this.resetSelectionsForAudience(respondentSelection.selectionTarget);
      this.resetInvalidAttachedModule();
      this.resetInvalidPostAnswerActions();
      this.resetInvalidQuestionTypes();
    },
    clearAudienceSelectionFields() {
      this.survey.respondentSelectionType = null;
      this.survey.selectedSurveyUserIds = [];
      this.survey.selectAllSurveyUsers = false;
      this.survey.surveyUserFilter = {};
      this.survey.surveyUserAutoCondition = {};
      this.survey.selectedSubOrganizationIds = [];
      this.survey.selectAllSubOrganizations = false;
      this.survey.subOrganizationFilter = {};
    },
    resetSelectionsForAudience(selectionTarget) {
      if (selectionTarget !== "subOrganization") {
        this.survey.selectedSubOrganizationIds = [];
        this.survey.selectAllSubOrganizations = false;
        this.survey.subOrganizationFilter = {};
      }

      if (selectionTarget !== "surveyUser") {
        this.survey.selectedSurveyUserIds = [];
        this.survey.selectAllSurveyUsers = false;
        this.survey.surveyUserFilter = {};
        this.survey.surveyUserAutoCondition = {};
      }
    },
    resetInvalidAttachedModule() {
      if (!this.showAttachedModuleField) {
        this.survey.attachedModule = null;
        return;
      }

      if (
        this.survey.attachedModule
        && !isAttachedModuleAllowedForAudience(
          this.survey.respondentType,
          this.survey.attachedModule,
          this.surveyBuilderConfig
        )
      ) {
        this.survey.attachedModule = null;
      }
    },
    resetInvalidPostAnswerActions() {
      this.survey.postAnswerActions = normalizePostAnswerActionsForAudience(
        this.survey.postAnswerActions,
        this.survey.respondentType,
        this.surveyBuilderConfig
      );
    },
    resetInvalidQuestionTypes() {
      this.survey.allowedQuestionTypes = normalizeQuestionTypesForAudience(
        this.survey.allowedQuestionTypes,
        this.survey.respondentType,
        this.surveyBuilderConfig
      );
    },
    normalizeQuestionTypes(values) {
      if (!values || values.length === 0) {
        this.survey.allowedQuestionTypes = ["all"];
        return;
      }

      if (values.includes("all") && values.length > 1) {
        this.survey.allowedQuestionTypes = values[values.length - 1] === "all"
          ? ["all"]
          : values.filter((item) => item !== "all");
      }

      this.resetInvalidQuestionTypes();
    },
    openSelectionDialog() {
      if (this.currentRespondentSelectionOption?.selectionTarget === "subOrganization") {
        this.selectSubOrganizationsModal = true;
      } else {
        this.selectUsersModal = true;
      }
    },
    setSelectedSubOrganizations({ selected, selectAll, filter }) {
      this.survey.selectedSubOrganizationIds = selected.map((item) => item.id);
      this.survey.selectAllSubOrganizations = !!selectAll;
      this.survey.subOrganizationFilter = filter || {};
      this.selectSubOrganizationsModal = false;
    },
    setSelectedSurveyUsers({ selected, selectAll, filter, autoCondition }) {
      this.survey.selectedSurveyUserIds = selected.map((item) => item.mongoId);
      this.survey.selectAllSurveyUsers = !!selectAll;
      this.survey.surveyUserFilter = filter || {};
      this.survey.surveyUserAutoCondition = autoCondition || {};
      this.selectUsersModal = false;
    },
    openScheduleDatePicker(type) {
      const isEndDate = type === "end";
      const selectedDate = isEndDate ? this.survey.endDate : this.survey.startDate;
      const fallbackDate = isEndDate ? this.survey.startDate || this.today : this.today;

      this.scheduleDateMode = type;
      this.scheduleDraftDate = selectedDate || fallbackDate;
      this.scheduleDateDialog = true;
    },
    closeScheduleDatePicker() {
      this.scheduleDateDialog = false;
      this.scheduleDraftDate = null;
    },
    clearScheduleDraftDate() {
      this.scheduleDraftDate = null;
    },
    confirmScheduleDate() {
      if (!this.scheduleDraftDate) {
        return;
      }

      if (this.scheduleDateMode === "end") {
        this.survey.endDate = this.scheduleDraftDate;
      } else {
        this.survey.startDate = this.scheduleDraftDate;
      }

      this.closeScheduleDatePicker();
    },
    formatScheduleDate(value) {
      if (!value) return "";
      const date = new Date(`${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    validateSelections() {
      if (!this.showRespondentSelectionOptions) {
        return true;
      }

      const selectionOption = this.currentRespondentSelectionOption;

      if (!selectionOption) {
        this.$notify({
          title: this.surveyBuilderConfig.validationMessages.respondentSelectionInvalid,
          type: "error",
        });
        return false;
      }

      if (
        selectionOption.requiresSelection
        && !(this.survey[selectionOption.selectionKey] || []).length
      ) {
        this.$notify({ title: selectionOption.emptyMessage, type: "error" });
        return false;
      }

      return true;
    },
    validateAttachedModule() {
      const validationMessage = validateAttachedModuleValue(
        this.survey.respondentType,
        this.survey.attachedModule,
        this.surveyBuilderConfig
      );

      if (validationMessage === true) {
        return true;
      }

      this.$notify({ title: validationMessage, type: "error" });
      return false;
    },
    validateSchedule() {
      if (!this.survey.scheduleEnabled) {
        this.scheduleValidationTouched = false;
        return true;
      }

      this.scheduleValidationTouched = true;

      if (this.scheduleStartDateError || this.scheduleEndDateError) {
        this.$notify({
          title: this.scheduleStartDateError || this.scheduleEndDateError,
          type: "error",
        });
        return false;
      }

      return true;
    },
    buildPayload() {
      this.resetInvalidPostAnswerActions();
      this.resetInvalidQuestionTypes();

      const payload = {
        title: this.survey.title,
        description: this.survey.description,
        instructions: this.survey.instructions,
        type: this.survey.type,
        respondentType: this.survey.respondentType,
        isDisabled: this.survey.isDisabled,
        isScoreEnabled: this.score,
        scheduleEnabled: this.survey.scheduleEnabled,
        startDate: this.survey.scheduleEnabled ? this.survey.startDate : null,
        endDate: this.survey.scheduleEnabled ? this.survey.endDate : null,
        attachedModule: this.showAttachedModuleField ? this.survey.attachedModule : null,
        postAnswerActions: this.survey.postAnswerActions,
        allowedQuestionTypes: this.survey.allowedQuestionTypes,
        isRepeatQuestionEnabled: this.survey.isRepeatQuestionEnabled,
        multiStepType: this.survey.multiStepType,
        multiStepQuestionCount: this.survey.multiStepQuestionCount,
        orgId: this.orgId,
        createdBy: this.loggedInUser,
      };

      if (this.showRespondentSelectionOptions) {
        Object.assign(payload, {
          respondentSelectionType: this.survey.respondentSelectionType,
          selectedSubOrganizationIds: this.survey.selectedSubOrganizationIds,
          selectedSurveyUserIds: this.survey.selectedSurveyUserIds,
          selectAllSubOrganizations: this.survey.respondentSelectionType === "All Sub-Organizations" || this.survey.selectAllSubOrganizations,
          selectAllSurveyUsers: this.survey.respondentSelectionType === "All Users" || this.survey.selectAllSurveyUsers,
          surveyUserFilter: this.survey.surveyUserFilter,
          surveyUserAutoCondition: this.survey.surveyUserAutoCondition,
          subOrganizationFilter: this.survey.subOrganizationFilter,
        });
      }

      return payload;
    },
    async saveNewSurvey() {
      try {
        if (
          !this.$refs.addNewSurvey.validate()
          || !this.validateSchedule()
          || !this.validateAttachedModule()
          || !this.validateSelections()
        ) {
          return;
        }

        this.survey.isScoreEnabled = this.score;
        this.startLoading();
        const payload = this.buildPayload();

        const data = this.updateData && this.updateData._id
          ? await EsgService.updateAssessment(this.updateData._id, payload)
          : await EsgService.createAssessment(payload);

        if (!data.success) {
          throw new Error(data.message || this.$t("esgPlatform.errorSavingAssessment"));
        }

        this.$notify({
          title: this.updateData && this.updateData._id ? this.$t("esgPlatform.updatedSurveySuccess") : this.$t("esgPlatform.createdSurveySuccess"),
          type: "success",
        });

        if (this.fromAddGoals || (this.updateData && this.updateData._id)) {
          this.$emit("surveyCreated", { surveyId: data.data._id });
          this.$emit("closeModal");
        } else {
          this.$router.push({
            name: this.isEsgContext ? "ESGSurveyDetails" : "SurveyDetails",
            params: { id: data.data._id },
          });
        }
      } catch (err) {
        console.error(err);
        const errorMessage = err?.data?.message || err?.response?.data?.message || err?.message || this.$t("esgPlatform.errorSavingAssessment");
        this.$notify({
          title: errorMessage,
          type: "error",
        });
      } finally {
        this.stopLoading();
      }
    },
    closeModal() {
      this.$emit("closeModal");
    },
  },
};
</script>

<style scoped lang="scss">
.survey-dialog-card {
  border-radius: 0;
  font-family: "Poppins", sans-serif;
  overflow: hidden;
}

.survey-modal-toolbar .v-toolbar__content {
  display: flex;
  align-items: center;
  width: 100%;
}

.survey-modal-title {
  flex: 1;
}

.survey-close-btn {
  margin-left: auto !important;
}

.survey-modal-body {
  padding: 34px 30px 20px;
  overflow-y: auto;
  max-height: calc(90vh - 56px - 80px);
}

.survey-section {
  padding-bottom: 30px;
  border-bottom: 1px solid #d2d2d2;

  & + .survey-section {
    padding-top: 30px;
  }
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  color: #1e1e1e;
  font-family: "Poppins", sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 20px;
  user-select: none;
}

.section-title__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:hover .section-title__icon,
  &:focus .section-title__icon {
    color: #0ebf67 !important;
  }

  &:focus {
    outline: none;
  }
}

.section-title__icon {
  color: #666666 !important;
  font-size: 50px !important;
  height: 20px !important;
  width: 20px !important;
  transition: transform 0.25s ease, color 0.2s ease;
  flex-shrink: 0;

  &--collapsed {
    transform: rotate(-180deg);
  }
}

.survey-grid {
  margin: 0 -12px !important;
}

.survey-grid__col {
  padding: 0 12px !important;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #1e1e1e;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.field-label__text {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.field-label__required {
  line-height: 1;
}

.field-label__tooltip-icon {
  align-self: center;
  font-size: 13px !important;
}

.survey-field {
  margin-bottom: 20px !important;
}

.setting-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.setting-block--below-select {
  margin-top: 20px;
}

.setting-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 36px;
}

.setting-toggle__label {
  min-width: 58px;
  color: #000000;
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 1;
}

.survey-inner-divider {
  margin: 20px 0 !important;
  border-color: #d2d2d2 !important;
}

.schedule-control {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 22px;

  .field-label {
    margin-bottom: 0;
  }

  .field-label__text {
    color: #333333;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
  }
}

.schedule-date-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
  margin-bottom: 18px;
}

.schedule-date-link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #333333;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;

  &:hover,
  &:focus {
    color: #0ebf67;
    outline: none;
  }
}

.schedule-date-link--error {
  color: #d32f2f;
}

.schedule-date-link__icon {
  color: #00bd73;
  font-size: 24px !important;
}

.schedule-date-link__value {
  margin-left: 8px;
  color: #787878;
}

.schedule-date-error {
  margin: 4px 0 0 34px;
  color: #d32f2f;
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  line-height: 16px;
}

.schedule-date-dialog-card {
  overflow: hidden;
  border-radius: 6px !important;
  background: #fafafa;
  font-family: "Poppins", sans-serif;
}

.schedule-date-dialog-toolbar {
  height: 76px !important;
}

::v-deep .schedule-date-dialog-toolbar .v-toolbar__content {
  height: 76px !important;
  padding: 0 28px;
}

.schedule-date-dialog-title {
  color: #ffffff;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
}

.schedule-date-dialog-close {
  width: 44px !important;
  height: 44px !important;
}

.schedule-date-dialog-close ::v-deep .v-icon {
  font-size: 36px !important;
}

.schedule-date-dialog-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 690px;
  padding: 56px 100px 58px !important;
  background: #fafafa;
}

.schedule-calendar {
  width: 390px;
  max-width: 100%;
  background: transparent !important;
  box-shadow: none !important;
}

::v-deep .schedule-calendar.v-picker {
  border-radius: 0;
  box-shadow: none;
}

::v-deep .schedule-calendar .v-picker__body {
  width: 100% !important;
  background: transparent;
}

::v-deep .schedule-calendar .v-date-picker-header {
  padding: 0 0 18px;
}

::v-deep .schedule-calendar .v-date-picker-header button {
  color: #00bd73 !important;
}

::v-deep .schedule-calendar .v-date-picker-header__value button {
  color: #000000 !important;
  font-family: "Poppins", sans-serif;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

::v-deep .schedule-calendar .v-date-picker-table {
  height: 300px;
  padding: 0;
}

::v-deep .schedule-calendar .v-date-picker-table th {
  height: 42px;
  color: #aeb9c0 !important;
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  font-weight: 400;
}

::v-deep .schedule-calendar .v-date-picker-table td {
  height: 48px;
  padding: 0;
}

::v-deep .schedule-calendar .v-date-picker-table .v-btn {
  width: 44px;
  height: 44px;
  color: #0b2c3d;
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  font-weight: 400;
}

::v-deep .schedule-calendar .v-date-picker-table .v-btn.v-btn--active {
  color: #ffffff !important;
}

::v-deep .schedule-calendar .v-date-picker-table .v-btn.v-btn--disabled {
  color: #b7c1c9 !important;
}

.schedule-selected-date {
  width: 390px;
  max-width: 100%;
  margin-top: 22px;
}

.schedule-selected-date__label {
  color: #111111;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.35;
}

.schedule-selected-date__value {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 28px;
  margin-top: 24px;
  color: #111111;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
}

.schedule-selected-date__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:focus {
    outline: none;
  }
}

.schedule-selected-date__clear ::v-deep .v-icon {
  color: #d7d7d7;
  font-size: 20px !important;
}

.schedule-date-dialog-select {
  width: 304px !important;
  height: 60px !important;
  margin-top: 78px;
  border-radius: 6px !important;
  box-shadow: none !important;
  color: #ffffff !important;
  font-family: "Poppins", sans-serif;
  font-size: 16px !important;
  font-weight: 700;
  letter-spacing: 0;
}

.select-users-btn {
  min-width: 130px !important;
  padding: 0 24px !important;
  border-radius: 5px !important;
  box-shadow: none !important;
  font-family: "Poppins", sans-serif;
  font-size: 14px !important;
  font-weight: 600;
  letter-spacing: 0;
}

.survey-radio-group {
  margin-top: 16px !important;
  margin-bottom: 0 !important;
  padding-top: 0;
}

.survey-footer-divider {
  margin-top: 30px;
  border-color: #d2d2d2 !important;
}

.survey-actions {
  padding: 20px 0 8px !important;
  gap: 16px;
  justify-content: flex-end;
}

.survey-btn {
  min-width: 103px !important;
  height: 44px !important;
  padding: 0 28px !important;
  border-radius: 5px !important;
  box-shadow: none !important;
  font-family: "Poppins", sans-serif;
  font-size: 14px !important;
  font-weight: 600;
  letter-spacing: 0;
}

.survey-btn--secondary {
  margin-right: 0 !important;
}

::v-deep .v-input {
  font-family: "Poppins", sans-serif;
}

::v-deep .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

::v-deep .v-text-field--outlined .v-input__slot,
::v-deep .v-select.v-text-field--outlined .v-input__slot {
  min-height: 48px;
  margin-bottom: 0 !important;
  border-radius: 5px;
  border-color: #d2d2d2 !important;
  box-shadow: none !important;
  align-items: center !important;
}

::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
  display: flex;
  align-items: center;
}

::v-deep .v-input__append-inner .v-icon {
  color: #7c7c7c !important;
  font-size: 20px !important;
}

::v-deep .v-text-field--outlined input,
::v-deep .v-select__selection,
::v-deep .v-select__selection--comma {
  color: #000000;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

::v-deep .v-input input::placeholder,
::v-deep .v-select .v-select__selection:empty::before {
  color: #787878 !important;
  opacity: 1;
}

::v-deep .v-text-field > .v-input__control > .v-input__slot,
::v-deep .v-select > .v-input__control > .v-input__slot {
  padding-left: 15px !important;
  padding-right: 8px !important;
}

::v-deep .v-input__control {
  min-height: auto;
}

::v-deep .v-messages {
  min-height: 0;
}

::v-deep .survey-radio-group .v-input--radio-group__input {
  gap: 8px;
}

::v-deep .v-radio {
  margin-bottom: 0;
}

::v-deep .v-radio .v-label {
  color: #000000 !important;
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
}

::v-deep .v-label {
  color: #111111;
  font-size: 14px;
}

::v-deep .v-input--switch .v-input__slot {
  margin-bottom: 0;
}

::v-deep .v-input--switch .v-input--selection-controls__input {
  width: 52px;
  height: 28px;
  margin-right: 0;
}

::v-deep .v-input--switch .v-input--switch__track {
  height: 28px;
  width: 52px;
  border-radius: 999px;
  opacity: 1;
}

::v-deep .v-input--switch .v-input--switch__thumb {
  height: 20px;
  width: 20px;
}

::v-deep .v-input--switch:not(.v-input--is-label-active) .v-input--switch__track {
  color: #8a8a8a;
}

::v-deep .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: #0ebf67;
}

::v-deep .v-input--switch.v-input--is-label-active .v-input--switch__thumb {
  color: #ffffff;
}

::v-deep .icon-with-background {
  font-size: 14px;
  cursor: pointer;
  border-radius: 50%;

  &:focus {
    outline: 2px solid #0ebf67;
    outline-offset: 1px;
  }
}

::v-deep .survey-editor-card {
  margin-bottom: 20px !important;
  border-radius: 5px;
  border-color: #d2d2d2;
  box-shadow: none;
}

::v-deep .survey-editor-card .ql-toolbar {
  display: none;
}

::v-deep .ql-container {
  min-height: 140px;
  border: 0;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
}

::v-deep .ql-editor {
  min-height: 140px;
  padding: 10px 10px 8px 15px;
  color: #333333;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

::v-deep .ql-editor.ql-blank::before {
  left: 15px;
  right: 10px;
  color: #787878;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-style: normal;
}

::v-deep .esg-program-display .v-input__slot {
  pointer-events: none;
  cursor: default;
}
.score-switch {
  margin-top: 0;

  ::v-deep .v-input__control {
    min-height: 32px;
  }

  ::v-deep .v-input--selection-controls__input {
    margin-right: 0;
  }

  ::v-deep .v-input--switch__track {
    background-color: #787878;
    opacity: 1;
  }

  ::v-deep .v-input--switch__thumb {
    background-color: #ffffff;
  }

  &.v-input--is-label-active {
    ::v-deep .v-input--switch__track {
      background-color: #0ebf67;
    }
  }
}

@media (max-width: 959px) {
  .survey-modal-body {
    padding: 24px 16px 16px;
    max-height: calc(100vh - 56px - 72px);
  }

  .survey-grid {
    margin: 0 -8px !important;
  }

  .survey-grid__col {
    padding: 0 8px !important;
  }

  .survey-grid__col + .survey-grid__col {
    margin-top: 0;
  }

  .setting-block--below-select {
    margin-top: 16px;
  }
}

@media (max-width: 599px) {
  .survey-modal-body {
    padding: 16px 12px 12px;
  }

  .section-title {
    font-size: 15px;
    margin-bottom: 16px;
  }

  .survey-field {
    margin-bottom: 16px !important;
  }

  .schedule-control {
    flex-wrap: wrap;
    gap: 8px;
  }

  .schedule-date-dialog-body {
    min-height: 610px;
    padding: 34px 22px 40px !important;
  }

  ::v-deep .schedule-date-dialog-toolbar .v-toolbar__content {
    padding: 0 20px;
  }

  .schedule-date-dialog-title {
    font-size: 24px;
  }

  .schedule-date-dialog-select {
    width: 100% !important;
    max-width: 304px;
    margin-top: 54px;
  }
}
</style>
